import moment from "moment";

import { eventStatuses } from "../../../../utils/Constants";
import {
  CUSTOM_PLAN_ERRORS,
  EVENT_MODELS,
  getEventBillingModel,
} from "../../Form/Steps/ChooseCrewSize/crewSizeUtils";
import { getEventRemainingDays } from "../Roles/crewSizeUpgradeUtils";

export const CUSTOM_PLAN_UPGRADE_ERRORS = {
  ...CUSTOM_PLAN_ERRORS,
  /** The crew size can't decrease, because there are no refunds. */
  notAboveCurrent: "notAboveCurrent",
  /** The custom rate can't go down either (PROLINE-4107). */
  belowCurrentRate: "belowCurrentRate",
};

/** A custom plan is upgraded only while the event is still to run or running. */
export const canUpgradeCustomPlan = (event) =>
  [eventStatuses.upcoming, eventStatuses.ongoing].includes(event?.status);

/** Billed as an extended event: follows `payment_plan`, frozen at publishing. */
export const isExtendedEvent = (event) =>
  getEventBillingModel(event, event?.operating_days) === EVENT_MODELS.extended;

/**
 * Validity of the "Update Custom Plan" fields against the current plan: the new ceiling
 * must be a whole number above the current one, and the rate above 0 and not below the
 * current rate.
 */
export const getCustomPlanUpgradeState = (plan, currentCeiling, currentRate) => {
  const applicants = String(plan?.applicants ?? "").trim();
  const rate = String(plan?.rate ?? "").trim();
  const applicantsNumber = Number(applicants);

  let applicantsError = null;
  if (!applicants) {
    applicantsError = CUSTOM_PLAN_UPGRADE_ERRORS.required;
  } else if (
    !Number.isInteger(applicantsNumber) ||
    applicantsNumber <= currentCeiling
  ) {
    applicantsError = CUSTOM_PLAN_UPGRADE_ERRORS.notAboveCurrent;
  }

  let rateError = null;
  if (!rate) {
    rateError = CUSTOM_PLAN_UPGRADE_ERRORS.required;
  } else if (!(Number(rate) > 0)) {
    rateError = CUSTOM_PLAN_UPGRADE_ERRORS.invalidRate;
  } else if (currentRate != null && Number(rate) < currentRate) {
    rateError = CUSTOM_PLAN_UPGRADE_ERRORS.belowCurrentRate;
  }

  return { isValid: !applicantsError && !rateError, applicantsError, rateError };
};

/**
 * Summary of a custom plan upgrade, previewed while the Super Admin types (PROLINE-4107 §3):
 * daily → (new rate − current rate) × remaining operating days, today included;
 * extended → new rate − current rate, with no remaining period. Never below 0.
 * The days are the backend's; without them the daily difference is unknown (null).
 */
export const getCustomPlanUpgradeSummary = ({ event, currentRate, newRate }) => {
  const increase = Math.max(0, newRate - currentRate);
  if (isExtendedEvent(event)) return { extended: true, difference: increase };
  const days = getEventRemainingDays(event);
  return {
    extended: false,
    remainingDays: days.remaining,
    totalDays: days.total,
    difference: days.remaining == null ? null : increase * days.remaining,
  };
};

/** Calendar months of the event left, counting the current one, out of its months. */
export const getRemainingMonths = (startAt, endAt, now = new Date()) => {
  const end = moment(endAt).locale("en").startOf("month");
  const monthsFrom = (from) =>
    Math.max(0, end.diff(moment(from).locale("en").startOf("month"), "months") + 1);
  return {
    total: monthsFrom(startAt),
    remaining: monthsFrom(moment.max(moment(now), moment(startAt))),
  };
};

/**
 * "Upgrade Plan" of a published event on a standard plan: a higher tier, or a custom size
 * and rate, previewed while the Super Admin picks it. Daily → (new − current) daily fee ×
 * the backend's remaining operating days (PROLINE-4107 §3); extended → per month × the
 * remaining months (Figma). Never below 0, because there are no refunds.
 */
export const getCategoryUpgradeSummary = ({
  event,
  currentFee,
  newFee,
  now = new Date(),
}) => {
  const increase = Math.max(0, newFee - (currentFee || 0));
  if (isExtendedEvent(event)) {
    const months = getRemainingMonths(event?.event_start_at, event?.event_end_at, now);
    return {
      monthly: true,
      remaining: months.remaining,
      total: months.total,
      difference: increase * months.remaining,
    };
  }
  const days = getEventRemainingDays(event);
  return {
    monthly: false,
    remaining: days.remaining,
    total: days.total,
    difference: days.remaining == null ? null : increase * days.remaining,
  };
};
